import { FlySvgSprite } from '@fly/svg';
import * as Accordion from '@radix-ui/react-accordion';
import cn from 'classnames';

/** `accordion_chevron` asset is drawn pointing down; rotation is relative to that. */
export type FlyAccordionChevronClosedDirection =
  | 'down'
  | 'up'
  | 'right'
  | 'left';

const CHEVRON_ROTATION_BY_DIRECTION: Record<
  FlyAccordionChevronClosedDirection,
  string
> = {
  down: 'rotate-0 group-data-[state=open]:rotate-180',
  up: 'rotate-180 group-data-[state=open]:rotate-0',
  right: '-rotate-90 group-data-[state=open]:rotate-0',
  left: 'rotate-90 group-data-[state=open]:-rotate-90',
};

export const FlyAccordion = ({
  trigger,
  visibleContent,
  children,
  className,
  triggerClassName,
  inlineVisibleContent = false,
  chevronClosedDirection = 'down',
  chevronClassName,
  defaultOpen = false,
  open,
  onOpenChange,
}: {
  trigger: React.ReactNode;
  children: React.ReactNode;
  visibleContent?: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  inlineVisibleContent?: boolean;
  chevronClosedDirection?: FlyAccordionChevronClosedDirection;
  chevronClassName?: string;
  defaultOpen?: boolean;
  /** When set, controls open state (survives parent re-renders when lifted). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const chevronRotation = CHEVRON_ROTATION_BY_DIRECTION[chevronClosedDirection];

  const triggerNode = (
    <Accordion.Header className={cn({ 'shrink-0': inlineVisibleContent })}>
      <Accordion.Trigger className={cn('group', triggerClassName)}>
        <div className='flex items-center gap-[6px]'>
          <FlySvgSprite
            name='accordion_chevron'
            size={16}
            className={cn(
              'transition-transform duration-300',
              chevronRotation,
              chevronClassName
            )}
          />
          {trigger}
        </div>
      </Accordion.Trigger>
    </Accordion.Header>
  );

  return (
    <Accordion.Root
      type='single'
      collapsible
      {...(open !== undefined
        ? { value: open ? 'item-1' : '' }
        : { defaultValue: defaultOpen ? 'item-1' : undefined })}
      onValueChange={(value) => {
        onOpenChange?.(value === 'item-1');
      }}
    >
      <Accordion.Item value='item-1' className={className}>
        {inlineVisibleContent ? (
          <div className='flex'>
            {triggerNode}
            <div className='min-w-0 flex-1'>{visibleContent}</div>
          </div>
        ) : (
          <>
            {triggerNode}
            {visibleContent}
          </>
        )}
        <Accordion.Content className='overflow-hidden data-[state=closed]:animate-radix-slide-up data-[state=open]:animate-radix-slide-down'>
          {children}
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
